import React, { useState, useCallback } from 'react';
import moment from 'moment';
import { Icon, Button, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { VersionView, PublishList, ListView, DocumentContainer, ComplexArtboardWrapper, VersionMenu } from './styles';
import NoSuchEnvironment from '../../artboard/NoSuchEnv';
import { moveToQaComplete, publishDesign } from '../../utils/services/complexDocumentsAPI';
import { COMPLEX_DOCUMENT_VERSION, DESIGN_TYPE } from '../../utils/complexDocumentConstants';
import { TEMPLATE_ENVIRONMENT_PAGE } from '../../artboard/PublishOverlay/translationValues';
import { useSessionContext } from '../../context/sessionContext';

import {
  DraftIcon,
  PublishVersionIcon,
  NotYetTestedIcon,
  ForApprovalIcon,
  PublishIcon,
  testingIcon,
} from '../../images/svgIcons';

import ComplexArtboardProvider, { CONTEXT_KEYS, useArtboardContext } from '../../context/complexArtboardContext';
import ComplexDocumentHistoryProvider, { COMPLEX_DOCUMENT_CONTEXT_KEYS, useDocumentContext } from '../../context/complexDocumentContext';
import BusinessLogicProvider, { BusinessLogic } from '../../context/businessLogicContext';
import FontsProvider from '../../context/fontsContext';

import ComplexArtboard from './ComplexArtboard';

const NAMESPACES = ['TEMPLATE_ENVIRONMENT_PAGE'];
const OVERRIDE_ARTBOARD_STATE = {
  [CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN]: false,
  [CONTEXT_KEYS.ENABLE_RULER]: false
}

const checkUserAccess = (userType, roles, userDetails = {}) => {
  if (roles) {
    return (
      userDetails?.userType === userType && userDetails.roles.some(role => roles.includes(role))
    );
  } else {
    return userDetails?.userType === userType;
  }
};

const ComplexDocumentVersion = ({ documentId, onClose, editable, currentDesign = DESIGN_TYPE.DRAFT }) => {

  const { t } = useTranslation(NAMESPACES);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userData] = useSessionContext(sessionSelector);

  const [currentEnv, setCurrentEnv] = useState(COMPLEX_DOCUMENT_VERSION.QA_READY);

  const [awaitingApiResponse, setAwaitingApiResponse] = useState(false);

  const lifecycleSelector = useCallback(store => store[CONTEXT_KEYS.LIFECYCLE_TIMELINE], []);
  const [lifeCycle, updateArtboard] = useArtboardContext(lifecycleSelector, `ComplexDocumentVersion`);

   const handleChange = useCallback(async type => {
    if (type === COMPLEX_DOCUMENT_VERSION.DRAFT) {
      onClose();
    } else {
      setCurrentEnv(type);
    }
    /*let response;
    if (type !== currentEnv) {
      onLoading(true);
      switch (type) {
        case 'publish':
          response = await getPublishedData(currentId);
          break;
        case 'testing':
          response = await getTestingData(currentId);
          break;
        default:
          break;
      }
      setCurrentEnv(type);
      const result = {
        ...templateData,
        [type]: response,
      };

      let pageData = defaultPage;
      if (response?.pages?.length > 0) {
        pageData = await json2Element(response, '', globalVariables, t);
      }
      if (type === 'draft') {
        updatePageSettings(Object.assign({}, draftPageSettings));
        setSendForTesting({
          sendToTest: true,
          testVersion: true,
        });
        onCancel(templateData.draft);
      } else {
        setSendForTesting({
          sendToTest: result[type] ? true : false,
          testVersion: type === 'testing',
        });
        handleElements(pageData);
        updateTempPageSettings(response);
      }
      onLoading(false);
      setTemplateData(result);
      scrollView.scrollTop = 0;
    }*/
  }, [onClose]);

  const designType = currentDesign === 'document' ? "document" : currentDesign === 'flow' ? 'Reusable Page' : "Reusable section";

  const onComplete = useCallback(async () => {
    setAwaitingApiResponse(true);
    let response = await moveToQaComplete(documentId, currentDesign);
    if (response) {
      if (response && response.errors && response.errors.length > 0) {
        console.error("Error in API :: ", response);
        message.error(t(
          TEMPLATE_ENVIRONMENT_PAGE.UNABLE_TO_PROCESS_ERROR_MESSAGE.key,
          TEMPLATE_ENVIRONMENT_PAGE.UNABLE_TO_PROCESS_ERROR_MESSAGE.value,
        ));
      } else {
        message.success(t(
          TEMPLATE_ENVIRONMENT_PAGE.SENT_TO_APPROVAL_MESSAGE.key,
          TEMPLATE_ENVIRONMENT_PAGE.SENT_TO_APPROVAL_MESSAGE.value, { type: designType }
        ));
        updateArtboard({
          [CONTEXT_KEYS.LIFECYCLE_TIMELINE]: Object.assign({}, lifeCycle, {
            [COMPLEX_DOCUMENT_VERSION.QA_COMPLETE]: new Date()
          })
        });
      }
    }
    setAwaitingApiResponse(false);
  }, [documentId, lifeCycle, currentDesign]);

  const onPublish = useCallback(async () => {
    setAwaitingApiResponse(true);
    let response = await publishDesign(documentId, currentDesign);
    if (response) {
      if (response && response.errors && response.errors.length > 0) {
        console.error("Error in API :: ", response);
        message.error(t(
          TEMPLATE_ENVIRONMENT_PAGE.UNABLE_TO_PROCESS_ERROR_MESSAGE.key,
          TEMPLATE_ENVIRONMENT_PAGE.UNABLE_TO_PROCESS_ERROR_MESSAGE.value,
        ));
      } else {       
        message.success(t(
          TEMPLATE_ENVIRONMENT_PAGE.PUBLISHED_SUCCESSFULLY_MESSAGE.key,
          TEMPLATE_ENVIRONMENT_PAGE.PUBLISHED_SUCCESSFULLY_MESSAGE.value, { type: designType }
        ));
        updateArtboard({
          [CONTEXT_KEYS.LIFECYCLE_TIMELINE]: Object.assign({}, lifeCycle, {
            [COMPLEX_DOCUMENT_VERSION.PUBLISHED]: new Date()
          })
        });
        setCurrentEnv(COMPLEX_DOCUMENT_VERSION.PUBLISHED);
      }
    }
    setAwaitingApiResponse(false);
  }, [documentId, lifeCycle, currentDesign]);

  const qareadystatus = lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_COMPLETE];
  const existingDocument = lifeCycle[currentEnv];

  const renderList = (type, label, icon) => {
    const noPublish = type === COMPLEX_DOCUMENT_VERSION.PUBLISHED && !lifeCycle[type];
    const noTesting = type === COMPLEX_DOCUMENT_VERSION.QA_READY && !lifeCycle[type];
    return (
      <ListView activeVersion={currentEnv === type} onClick={() => handleChange(type)}>
          <Icon component={icon} />
          <div className="column">
            <span>{label}</span>
            <span className={currentEnv === type ? 'note-text active' : 'note-text'}>
              {lifeCycle[type] ? moment(lifeCycle[type]).format('lll') : ''}
              {noPublish &&
                t(
                  TEMPLATE_ENVIRONMENT_PAGE.NOT_YET_PUBLISHED_LABEL.key,
                  TEMPLATE_ENVIRONMENT_PAGE.NOT_YET_PUBLISHED_LABEL.value,
                )}
              {noTesting &&
                t(
                  TEMPLATE_ENVIRONMENT_PAGE.TIME_NOT_AVAILABLE_LABEL.key,
                  TEMPLATE_ENVIRONMENT_PAGE.TIME_NOT_AVAILABLE_LABEL.value,
                )}
            </span>
          </div>
      </ListView>
    );
  };

  const publishAccess = checkUserAccess('master', ['PUBLISHER'], userData) || checkUserAccess('client', ['PUBLISHER'], userData);

  const isQaReadyLatest = lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_READY]
    ? lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_COMPLETE]
      ? lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_READY] > lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_COMPLETE]
      : true
    : false;

  const isQaCompleteLatest = !isQaReadyLatest && lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_COMPLETE]
    ? lifeCycle[COMPLEX_DOCUMENT_VERSION.PUBLISHED]
      ? lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_COMPLETE] > lifeCycle[COMPLEX_DOCUMENT_VERSION.PUBLISHED]
      : true
    : false;

  return (
    <VersionView>
      <div className="menu">
        <PublishList>
          {renderList(
            COMPLEX_DOCUMENT_VERSION.PUBLISHED,
            t(
              TEMPLATE_ENVIRONMENT_PAGE.PUBLISHED_ENV_TAB.key,
              TEMPLATE_ENVIRONMENT_PAGE.PUBLISHED_ENV_TAB.value,
            ),
            PublishVersionIcon,
          )}
          {renderList(
            COMPLEX_DOCUMENT_VERSION.QA_READY,
            t(
              TEMPLATE_ENVIRONMENT_PAGE.TESTING_ENV_TAB.key,
              TEMPLATE_ENVIRONMENT_PAGE.TESTING_ENV_TAB.value,
            ),
            qareadystatus ? testingIcon : NotYetTestedIcon,
          )}
          {renderList(
            COMPLEX_DOCUMENT_VERSION.DRAFT,
            t(
              TEMPLATE_ENVIRONMENT_PAGE.DRAFT_ENV_TAB.key,
              TEMPLATE_ENVIRONMENT_PAGE.DRAFT_ENV_TAB.value,
            ),
            DraftIcon,
          )}
        </PublishList>
      </div>
      <VersionMenu>
        {currentEnv === COMPLEX_DOCUMENT_VERSION.QA_READY &&
          editable &&
          lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_READY] && (
            <>
              {isQaReadyLatest && (
                <Button key="approval" onClick={!awaitingApiResponse && onComplete}>
                  { awaitingApiResponse 
                    ? <Icon type="loading" /> 
                    : <Icon component={ForApprovalIcon} /> }
                  {t(
                    TEMPLATE_ENVIRONMENT_PAGE.REQUEST_FOR_APPROVAL_BUTTON.key,
                    TEMPLATE_ENVIRONMENT_PAGE.REQUEST_FOR_APPROVAL_BUTTON.value,
                  )}
                </Button>
              )}
              {publishAccess && isQaCompleteLatest && (
                <Button type="primary" key="publish" onClick={!awaitingApiResponse && onPublish}>
                  { awaitingApiResponse 
                    ? <Icon type="loading" /> 
                    : <Icon component={PublishIcon} /> }
                  {t(
                    TEMPLATE_ENVIRONMENT_PAGE.PUBLISH_BUTTON.key,
                    TEMPLATE_ENVIRONMENT_PAGE.PUBLISH_BUTTON.value,
                  )}
                </Button>
              )}
            </>
          )}
        <Icon
          type="close"
          style={{
            margin: '0px 10px',
            color: '#000',
            fontSize: 15,
            backgroundColor: '#fff',
            padding: 8,
            borderRadius: '50%',
            cursor: 'pointer'
          }}
          onClick={() => {
            handleChange('draft');
          }}
        />
      </VersionMenu>
      {lifeCycle[currentEnv] ? (
        <ComplexArtboardWrapper className="readOnly versions">
          <ComplexDocumentHistoryProvider>
            <ComplexArtboardProvider documentId={documentId} customState={OVERRIDE_ARTBOARD_STATE} customName={"VersionContext"}>
              <BusinessLogicProvider>
                  <FontsProvider>
                    <DocumentContainer className={'environment'}>
                      <ComplexArtboard templateId={documentId} version={currentEnv} />
                    </DocumentContainer>
                  </FontsProvider>
              </BusinessLogicProvider>
            </ComplexArtboardProvider>
          </ComplexDocumentHistoryProvider>
        </ComplexArtboardWrapper>
      ) : (
        <div style={{ flex: 1, marginTop: 70 }}>
          <NoSuchEnvironment
            text={
              currentEnv === COMPLEX_DOCUMENT_VERSION.QA_READY
                ? t(
                    TEMPLATE_ENVIRONMENT_PAGE.DOCUMENT_NOT_SENT_FOR_TESTING.key,
                    TEMPLATE_ENVIRONMENT_PAGE.DOCUMENT_NOT_SENT_FOR_TESTING.value,
                  )
                : t(
                    TEMPLATE_ENVIRONMENT_PAGE.DOCUMENT_NOT_YET_PUBLISHED.key,
                    TEMPLATE_ENVIRONMENT_PAGE.DOCUMENT_NOT_YET_PUBLISHED.value,
                  )
            }
          />
        </div>
      )}
    </VersionView>
  )
}

export default ComplexDocumentVersion;